import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, FileText, Layers } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import CoinBadge from "../../components/CoinBadge";

export default function Examenes() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [exams, setExams] = useState([]);
    const [results, setResults] = useState([]);
    const [subject, setSubject] = useState("");
    const [confirm, setConfirm] = useState(null);

    useEffect(() => {
        const params = subject ? { subject } : {};
        api.get("/exams", { params }).then((r) => setExams(r.data || []));
    }, [subject]);

    useEffect(() => {
        async function loadHistory() {
            const ex = await api.get("/exams").then((r) => r.data || []);
            const all = [];
            for (const e of ex) {
                try {
                    const r = await api.get(`/exams/${e.id}/results`);
                    all.push(...(r.data || []));
                } catch (_) {}
            }
            all.sort((a, b) => (b.submitted_at || "").localeCompare(a.submitted_at || ""));
            setResults(all);
        }
        loadHistory();
    }, []);

    const subjects = [...new Set(exams.map((e) => e.subject))];

    // Group by (unit_number, subject)
    const grouped = {};
    exams.forEach((e) => {
        const key = `U${e.unit_number || "?"} · ${e.subject}`;
        if (!grouped[key]) grouped[key] = { unit_number: e.unit_number, unit_name: e.unit_name, subject: e.subject, items: [] };
        grouped[key].items.push(e);
    });
    const groupKeys = Object.keys(grouped).sort();

    return (
        <div className="p-6 md:p-10 fade-in" data-testid="examenes-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6 gap-4">
                <div>
                    <h1 className="text-3xl font-semibold tracking-tight">Exámenes de Práctica</h1>
                    <p className="text-edu-body mt-1">Exámenes organizados por unidad de tu grado.</p>
                </div>
                <CoinBadge amount={user.coin_balance} size="lg" />
            </div>

            <select
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="edu-input mb-6 min-w-[200px]"
                data-testid="exam-subject-filter"
            >
                <option value="">Filtrar por materia</option>
                {subjects.map((s) => (
                    <option key={s} value={s}>
                        {s}
                    </option>
                ))}
            </select>

            <div className="space-y-8 mb-10">
                {groupKeys.length === 0 && (
                    <p className="text-edu-muted">No hay exámenes aprobados aún.</p>
                )}
                {groupKeys.map((key) => {
                    const g = grouped[key];
                    return (
                        <section key={key}>
                            <h2 className="text-lg font-semibold mb-3 inline-flex items-center gap-2">
                                <Layers size={18} className="text-edu-navy" />
                                <span>
                                    Unidad {g.unit_number} - {g.subject}
                                </span>
                                {g.unit_name && (
                                    <span className="text-xs text-edu-muted font-normal">· {g.unit_name}</span>
                                )}
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {g.items.map((e) => (
                                    <div
                                        key={e.id}
                                        className="edu-card p-6"
                                        data-testid={`exam-card-${e.id}`}
                                    >
                                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                                            <span className="edu-pill bg-edu-navy/10 text-edu-navy">
                                                {e.subject}
                                            </span>
                                            {e.unit_number && (
                                                <span className="edu-pill bg-edu-gold-soft text-[#0A0A0A]">
                                                    U{e.unit_number}
                                                </span>
                                            )}
                                            <span className="inline-flex items-center gap-1 text-xs text-edu-muted">
                                                <Clock size={12} /> {e.duration_minutes} min
                                            </span>
                                        </div>
                                        <h3 className="text-lg font-semibold mb-3">{e.title}</h3>
                                        <div className="flex items-center justify-between">
                                            <CoinBadge amount={e.coin_cost} size="sm" />
                                            <button
                                                onClick={() => setConfirm(e)}
                                                className="edu-btn-primary px-4 py-2 text-sm font-medium"
                                                data-testid={`exam-take-${e.id}`}
                                            >
                                                Tomar Examen
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    );
                })}
            </div>

            <div className="edu-card p-6">
                <h3 className="text-lg font-semibold mb-4 inline-flex items-center gap-2">
                    <FileText size={18} /> Historial de exámenes
                </h3>
                {results.length === 0 ? (
                    <p className="text-sm text-edu-muted">Aún no has rendido exámenes.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="exam-history-table">
                            <thead>
                                <tr className="text-left border-b border-edu">
                                    <th className="py-2 font-semibold">Materia</th>
                                    <th className="py-2 font-semibold">Examen</th>
                                    <th className="py-2 font-semibold">Fecha</th>
                                    <th className="py-2 font-semibold">Nota</th>
                                    <th className="py-2 font-semibold">Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((r, i) => (
                                    <tr key={i} className="border-b border-edu/50">
                                        <td className="py-3">{r.subject}</td>
                                        <td className="py-3">{r.exam_title}</td>
                                        <td className="py-3 text-edu-muted">{(r.submitted_at || "").slice(0, 10)}</td>
                                        <td className="py-3 font-semibold">{r.score_percent}%</td>
                                        <td className="py-3">
                                            <span
                                                className={`edu-pill ${
                                                    r.grade === "Aprobado"
                                                        ? "bg-emerald-100 text-emerald-800"
                                                        : r.grade === "Intermedio"
                                                          ? "bg-yellow-100 text-yellow-800"
                                                          : "bg-red-100 text-red-800"
                                                }`}
                                            >
                                                {r.grade}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {confirm && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="edu-card p-6 max-w-md w-full" data-testid="exam-confirm-modal">
                        <h3 className="text-xl font-semibold mb-2">Confirmar examen</h3>
                        <p className="text-sm text-edu-body mb-3">
                            Vas a iniciar <strong>{confirm.title}</strong> ({confirm.subject}).
                        </p>
                        <ul className="text-sm space-y-1 mb-5 text-edu-body">
                            <li>Duración: {confirm.duration_minutes} minutos</li>
                            <li>Costo: {confirm.coin_cost} monedas</li>
                            <li>Tu saldo actual: {user.coin_balance} monedas</li>
                        </ul>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setConfirm(null)}
                                className="px-4 py-2 text-sm rounded-xl border border-edu hover:bg-gray-50"
                                data-testid="exam-confirm-cancel"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => navigate(`/alumno/examenes/${confirm.id}`)}
                                className="edu-btn-primary px-4 py-2 text-sm"
                                data-testid="exam-confirm-go"
                            >
                                Confirmar y Comenzar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
